import { ANNOUNCEMENT_ICON_XML as ICON } from '../../../shared/announcementIcons.js'
import { WORKER_SECTION_CARD_XML, WORKER_SECTION_HERO_XML } from '../../../shared/workerSectionArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import Prose from '../components/ds/Prose'
import FaqAccordion from '../components/ds/FaqAccordion'
import { findWorkJsonLd } from '../lib/landingJsonLd'

// /trust — the page that answers "is this legit", which every marketplace gets
// searched for and almost none answers. It is also where somebody deciding
// whether to hand over a passport goes before they do it.
//
// ⚠️ Claims here are promises (spec §8). Everything on this page should be
// checkable against what the platform actually does. Where we are not sure, say
// less rather than more.

const CHECKS = [
  { art: WORKER_SECTION_CARD_XML.aboutYou, title: 'Who you are',
    body: 'Photo ID, checked properly, once. Not a photocopy emailed to a stranger.' },
  { art: WORKER_SECTION_CARD_XML.work, title: 'Your right to work',
    body: 'Verified to the standard a workplace is legally obliged to hold on file.' },
  { art: WORKER_SECTION_CARD_XML.signing, title: 'Your registration',
    body: "Checked against the professional register, and watched after that so it can’t quietly lapse." },
  { art: WORKER_SECTION_CARD_XML.account, title: 'Your documents',
    body: 'The ones your role needs, with their expiry dates. We tell you before one runs out.' },
]

const FAQS = [
  { q: 'Is TempDash an agency?',
    a: 'No. We are a marketplace. The workplace and the professional agree the work directly with each other, and we provide the matching, the checks and the payments.' },
  { q: 'Who holds my money?',
    a: "A regulated payments provider, not TempDash. It’s yours the moment it lands in your wallet." },
  { q: 'What do you do with my documents?',
    a: "We check them, we hold them securely for as long as the law requires, and we show the workplace only that you’re cleared to work, not your documents themselves." },
  { q: 'Can a workplace see my personal details?',
    a: "They see what they need to know who is turning up and that you’re cleared to do the work. Your phone number stays yours." },
  { q: 'Who can post a shift?',
    a: "A verified workplace. We check a business is real and that it’s who it says it’s before it can post anything." },
  { q: "What if a workplace doesn’t pay?",
    a: "That’s our problem, not yours. Your pay is handled by us, not collected from the workplace by you." },
]

export default function Trust() {
  return (
    <div className="bg-canvas">
      <Seo
        title="Verification and trust"
        description="What we check before a workplace can post, what we check before you can work, who actually holds your money, and what happens to the documents you send us."
        path="/trust"
        jsonLd={findWorkJsonLd(FAQS)}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={WORKER_SECTION_HERO_XML.checking}
        eyebrow="Find work"
        title="You’re handing us a passport. Here is what we do with it."
        lead="Checks exist so the person next to you is who they say they are. That cuts both ways, and it’s the reason the work is worth having."
      />

      <Section ground="canvas">
        <SectionHead eyebrow="What we check on you" title={<>Once, and then we keep it current.</>} />
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {CHECKS.map((c) => (
            <div key={c.title}>
              <Art xml={c.art} className="h-[72px] w-[72px]" />
              <h3 className="mt-4 text-[21px] font-semibold">{c.title}</h3>
              <p className="mt-2 text-[17px] leading-relaxed text-ink-muted">{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section ground="navy">
        <div className="max-w-[720px]">
          <SectionHead on="navy" eyebrow="What we check on them" title={<>A workplace is checked before it can post anything.</>} />
          <p className="mt-5 text-[19px] leading-relaxed text-white/75">
            We verify the business is real, that it’s who it claims to be, and that the person
            posting is entitled to act for it. A shift on TempDash comes from somewhere we have
            looked at.
          </p>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <div className="space-y-12">
          <Prose id="not-an-agency" art={ICON.compliance} title="We are not an agency">
            <p>
              You are self employed. The workplace and you agree the work directly. We are the
              technology that connects you, runs the checks and moves the money, and we do not
              employ you, supply you or supervise you.
            </p>
            <p>
              That is not a technicality. It is why you choose your own shifts, your own rate
              bracket and your own days.
            </p>
          </Prose>

          <Prose id="your-money" art={ICON.wallet} title="Your money">
            <p>
              Held by a regulated payments provider, not by us. It is yours from the moment it
              lands in your wallet, ready to move to your bank whenever you want.
            </p>
            <p>
              If a workplace is slow or does not pay, that is a problem between us and them. You
              are paid for the work you did.
            </p>
          </Prose>

          <Prose id="your-data" art={ICON.safety} title="Your documents and your data">
            <p>
              We check your documents, hold them securely for as long as the law requires, and
              show a workplace only that you are cleared to work. They do not get copies.
            </p>
            <p>
              Your phone number stays yours. Nothing about your account is sold, and you can ask
              us for a copy of what we hold or ask us to delete it.
            </p>
            <p>
              The detail is in our <a className="text-link underline underline-offset-4" href="/legal/privacy">privacy notice</a>.
            </p>
          </Prose>

          <Prose id="if-it-goes-wrong" art={ICON.support} title="If something goes wrong">
            <p>
              Tell us on the shift. We get you to a person, and we deal with the workplace. You
              are not expected to argue about hours in a staff room or chase an invoice.
            </p>
          </Prose>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand title="Join on your own terms.">
        <Button href="/signup?for=work">Join TempDash</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
